import type {
	FieldValues,
	Path,
	PathValue,
	UseFormSetValue,
} from "react-hook-form";

import { useCallback, useState } from "react";

import { toKebabCaseId } from "../utils/shared/strings";

/** Keep external id in sync with name until the operator edits the id (useState forms). */
export const useNameBackedExternalId = (initialName = "") => {
	const [name, setName] = useState(initialName);
	const [externalId, setExternalId] = useState(() =>
		toKebabCaseId(initialName),
	);
	const [externalIdManual, setExternalIdManual] = useState(false);

	const onNameChange = useCallback(
		(next: string) => {
			setName(next);
			if (!externalIdManual) {
				setExternalId(toKebabCaseId(next));
			}
		},
		[externalIdManual],
	);

	const onExternalIdChange = useCallback((next: string) => {
		setExternalIdManual(true);
		setExternalId(next);
	}, []);

	return {
		externalId,
		name,
		onExternalIdChange,
		onNameChange,
	};
};

type NameBackedFields = {
	externalId: string;
	name: string;
};

/**
 * RHF variant: when name changes, auto-fill externalId until the operator edits it.
 * Forms must include `name` and `externalId` string fields.
 */
export const useNameBackedExternalIdSync = <
	T extends FieldValues & NameBackedFields,
>(
	setValue: UseFormSetValue<T>,
) => {
	const [externalIdManual, setExternalIdManual] = useState(false);

	const syncNameChange = useCallback(
		(next: string, fieldOnChange: (value: string) => void) => {
			fieldOnChange(next);
			if (!externalIdManual) {
				setValue(
					"externalId" as Path<T>,
					toKebabCaseId(next) as PathValue<T, Path<T>>,
					{ shouldDirty: true, shouldValidate: true },
				);
			}
		},
		[externalIdManual, setValue],
	);

	const syncExternalIdChange = useCallback(
		(next: string, fieldOnChange: (value: string) => void) => {
			setExternalIdManual(true);
			fieldOnChange(next);
		},
		[],
	);

	return { syncExternalIdChange, syncNameChange };
};
